import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Alert, Box, Button, Typography } from '@mui/material'

import { ReactComponent as ArrowRight } from '../../../../assets/icons/arrow-right.svg'
import { ReactComponent as Download } from '../../../../assets/icons/download-simple.svg'
import useTranslations from '../../../../i18n/useTranslations'
import useStyles from '../../../../ui/modules/shipmentsSearch/styles'
import useShippingsFiltersForm from './useShippingsFiltersForm'
import StatusCode from './StatusCode'
import ClientCenterCode from './ClientCenterCode'
import PostalCode from './PostalCode'
import Country from './Country'
import ServiceType from './ServiceType'
import DateRange from './DateRange'
import { useShippingFile } from '../../../../services/shippingsTracking'
import { useEffect, useState } from 'react'

const FiltersForm = ({
  userCentres,
  countries,
  filters,
  disabledFilterByStatusCode,
  cancel: handleCancelClick,
  confirm: applyFilters,
}) => {
  const classes = useStyles()
  const t = useTranslations()

  const { formik, handleSubmit } = useShippingsFiltersForm(
    applyFilters,
    filters,
    countries,
  )

  const combinedFilters = {
    ...filters,
    ...formik.values,
  }

  const cleanedFilters = Object.fromEntries(
    Object.entries(combinedFilters).filter(([_, value]) => value !== null),
  )

  const getFile = useShippingFile(cleanedFilters, false)
  const [isLoading, setIsLoading] = useState(false)
  const [showAlert, setShowAlert] = useState()

  const getShippingFile = async () => {
    setIsLoading(true)
    const res = await getFile.refetch()
    if (res?.error && res?.error?.data?.error?.error_code === 404)
      setShowAlert('shipping-tracking:error-file')
    else if (res?.error && res?.error?.data?.error?.error_code === 400)
      setShowAlert('shipping-tracking:error-date')
    else if (res?.error && res?.error?.data?.error?.error_code)
      setShowAlert('shipping-tracking:error-unexpected')
    else if (res?.data?.data?.length) window.open(res?.data?.data, '_blank')
    setIsLoading(false)
  }

  useEffect(() => {
    let timer
    if (showAlert !== null && showAlert !== 'shipping-tracking:error-date') {
      timer = setTimeout(() => {
        setShowAlert(null)
      }, 5000)
    }

    return () => clearTimeout(timer)
  }, [showAlert])

  useEffect(() => {
    setShowAlert(null)
  }, [formik.values, filters])

  return (
    <Box className={classes.content}>
      <Typography
        variant="h2"
        className={clsx(classes.title, 'secondaryTitle', 'decorator')}
      >
        {t('shipping-tracking:find-shipments')}
      </Typography>
      <form onSubmit={handleSubmit} noValidate>
        <Box className={classes.fields}>
          <ClientCenterCode centers={userCentres} {...formik} />
          <StatusCode
            {...formik}
            disabledFilterByStatusCode={disabledFilterByStatusCode}
          />
          <Country countries={countries} {...formik} />
          <PostalCode {...formik} />
          {/* <ServiceType centers={userCentres} {...formik} /> */}
          <DateRange {...formik} />
        </Box>

        <Box className={classes.actionButtons}>
          <Button
            onClick={handleCancelClick}
            variant="text"
            className={classes.downloadButton}
          >
            {t('action:cancel')}
          </Button>
          <Button
            type="submit"
            variant="outlined"
            className={classes.mainButton}
          >
            {t('action:visualise-selection')}
          </Button>

          <Button
            disabled={isLoading || showAlert === 'shipping-tracking:error-date'}
            className={classes.mainButton}
            onClick={() => {
              getShippingFile()
            }}
            endIcon={<Download />}
          >
            {t('action:download-excel-selection')}
          </Button>
        </Box>
        {showAlert ? (
          <Alert className={classes.alert} severity="error">
            {t(showAlert)}
          </Alert>
        ) : null}
      </form>
    </Box>
  )
}

FiltersForm.propTypes = {
  userCentres: PropTypes.oneOfType([
    ClientCenterCode.propTypes.centers,
    ServiceType.propTypes.centers,
  ]),
  countries: PropTypes.array,
  filters: PropTypes.object,
  disabledFilterByStatusCode: PropTypes.bool,
  cancel: PropTypes.func.isRequired,
  confirm: PropTypes.func.isRequired,
}

export default FiltersForm
